/**
 * Which metric-bar sections a counterparty's framework agreement covers.
 *
 * Passed only for a counterparty holding a single agreement. Its records are separate
 * Backoffice clients, so trades of the other family can still sit on it: this is a
 * decluttering hint, never a statement about what its book contains, and it may only
 * COLLAPSE a half that is empty anyway. Callers pair it with a presence signal for that
 * reason. An unknown/missing type (a counterparty with both agreements, or a multi-CP
 * "All Trades" view, which pass null) falls back to BOTH sections.
 *
 * Matching is case-insensitive: the backend may send 'gmra'/'GMRA'.
 */
export interface AgreementSections {
  showStockLB: boolean;
  showRepo: boolean;
}

/** The type to collapse the bar to: only for a counterparty holding exactly one agreement. */
export const singleAgreementType = <T extends string>(
  agreements: ReadonlyArray<{ type: T }> | undefined,
): T | null => (agreements?.length === 1 ? agreements[0].type : null);

export const agreementSections = (agreementType?: string | null): AgreementSections => {
  switch (agreementType?.toLowerCase()) {
    case 'gmra':
      return { showStockLB: false, showRepo: true };
    case 'gmsla':
      return { showStockLB: true, showRepo: false };
    default:
      return { showStockLB: true, showRepo: true };
  }
};

export interface RenderedSections {
  renderStockLB: boolean;
  renderRepo: boolean;
}

/**
 * Which metric-bar sections actually RENDER for a set of in-view rows.
 *
 * The agreement type only COLLAPSES an irrelevant, EMPTY section — it must never
 * hide a section that carries trades (LR-474 r5 #2): a CP onboarded under one
 * agreement can still hold the other family. So a section renders when the
 * agreement covers it OR any row of that family is present.
 */
export const renderedSections = (
  agreementType: string | null | undefined,
  rows: ReadonlyArray<{ trade_type: string }>,
): RenderedSections => {
  const { showStockLB, showRepo } = agreementSections(agreementType);
  return {
    renderStockLB:
      showStockLB || rows.some((r) => r.trade_type === 'lend' || r.trade_type === 'borrow'),
    renderRepo:
      showRepo || rows.some((r) => r.trade_type === 'repo' || r.trade_type === 'reverse_repo'),
  };
};
